<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import api from "@/services/apiService";

import ArgonButton from "@/components/ArgonButton.vue";
import BackendPagination from "@/components/BackendPagination.vue";
import noDataImg from "@/assets/img/illustrations/noData.png";
import ArgonAlert from "@/components/ArgonAlert.vue";
// State
const amenities = ref([]);
const errorMessage = ref("");
const loading = ref(true);
const router = useRouter();

const showModal = ref(false);
const isEditing = ref(false);
const selectedAmenity = ref(null);
const successMessage = ref("");

// Pagination
const currentPage = ref(1);
const itemsPerPage = 10;
const totalItems = ref(0);

const totalPages = computed(() =>
  Math.ceil(totalItems.value / itemsPerPage)
);

// Fetch amenities
const fetchAmenities = async () => {
  loading.value = true;
  try {
    const response = await api.get(`/amenities?page=${currentPage.value}`);
    amenities.value = response.data.member || [];
    totalItems.value = response.data.totalItems || 0;
  } catch (error) {
    errorMessage.value = error.response?.data?.message || "Error al obtener amenidades.";
  } finally {
    loading.value = false;
  }
};

// Handle pagination change
const changePage = async (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    await fetchAmenities();
  }
};

// Modal controls
const openModal = () => {
  isEditing.value = false;
  selectedAmenity.value = { name: "", description: "" };
  showModal.value = true;
};

const openEditModal = (amenity) => {
  isEditing.value = true;
  selectedAmenity.value = {
    "@id": amenity["@id"],
    name: amenity.name || "",
    description: amenity.description || "",
  };
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedAmenity.value = null;
  errorMessage.value = "";
};

// Save
const saveAmenity = async () => {
  try {
    if (!selectedAmenity.value.name || !selectedAmenity.value.description) {
      errorMessage.value = "Todos los campos son obligatorios.";
      return;
    }

    if (isEditing.value) {
      const amenityId = selectedAmenity.value["@id"].replace(/^\/api/, "");
      await api.patch(amenityId, {
        name: selectedAmenity.value.name,
        description: selectedAmenity.value.description,
      });
      successMessage.value = "Amenidad actualizada correctamente.";
    } else {
      const response = await api.post("/amenities", {
        name: selectedAmenity.value.name,
        description: selectedAmenity.value.description,
      });

      if (response?.data) {
        amenities.value.push(response.data);
        successMessage.value = `Amenidad "${selectedAmenity.value.name}" agregada correctamente.`;
      }
    }

    fetchAmenities();
    closeModal();

    setTimeout(() => {
      successMessage.value = "";
      errorMessage.value = "";
    }, 3000);
  } catch (error) {
    console.error("❌ Error al guardar amenidad:", error.response?.data || error.message);
    errorMessage.value = error.response?.data?.message || "Error al guardar la amenidad.";
    setTimeout(() => {
      errorMessage.value = "";
    }, 3000);
  }
};


// Delete
const deleteAmenity = async (amenity) => {
  if (!confirm(`¿Eliminar la amenidad "${amenity.name}"?`)) return;

  try {
    const amenityId = amenity["@id"].replace(/^\/api/, "");
    await api.delete(amenityId);
    fetchAmenities();

    successMessage.value = `Amenidad "${amenity.name}" eliminada correctamente.`;

    setTimeout(() => {
      successMessage.value = "";
    }, 3000);
  } catch (error) {
    console.error("❌ Error al eliminar amenidad:", error.response?.data || error.message);
    errorMessage.value = error.response?.data?.message || "Error al eliminar la amenidad.";
    setTimeout(() => {
      errorMessage.value = "";
    }, 3000);
  }
};


// On mount
onMounted(() => {
  const token = localStorage.getItem("auth_token");
  if (!token) {
    router.push("/signin");
  } else {
    fetchAmenities();
  }
});
</script>


<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0 d-flex">
            <h6>Amenidades</h6>
            <argon-button color="primary" size="sm" class="ms-auto" @click="openModal">
              <font-awesome-icon :icon="['fas', 'plus']" class="me-2" />
              Agregar Amenidad
            </argon-button>
          </div>

          <div class="card-body px-0 pt-0 pb-2">
            <div class="table-responsive p-0">
              <div v-if="loading" class="text-center py-3">
                <p class="text-secondary">Cargando amenidades...</p>
              </div>
              <div v-if="amenities && amenities.length === 0 && !loading && !errorMessage" class="text-center py-3">
                <img :src="noDataImg" alt="No hay datos disponibles" width="300" />
                <p class="text-secondary">No se encontraron amenidades</p>
              </div>

              <argon-alert v-if="successMessage" color="alert-success" icon="ni ni-check-bold"
                class="mb-2 text-dark w-50 ms-3">
                {{ successMessage }}
              </argon-alert>

              <argon-alert v-if="errorMessage && !showModal" color="alert-danger" icon="ni ni-fat-remove"
                class="mb-2 text-dark w-50 ms-3">
                {{ errorMessage }}
              </argon-alert>



              <table v-if="amenities && amenities.length > 0" class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Nombre</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Descripción
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"></th>

                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="amenity in amenities" :key="amenity['@id']">
                    <td>
                      <div class="d-flex px-2 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">{{ amenity.name }}</h6>
                        </div>
                      </div>
                    </td>

                    <td>
                      <p class="text-xs mb-0">{{ amenity.description }}</p>
                    </td>
                    <td class="text-end">
                      <font-awesome-icon :icon="['fas', 'pen-to-square']" class="me-3" @click="openEditModal(amenity)"
                        style="cursor:pointer;" />
                      <font-awesome-icon :icon="['fas', 'trash']" @click="deleteAmenity(amenity)"
                        style="cursor:pointer;" />
                    </td>
                  </tr>
                </tbody>
              </table>
              <BackendPagination :current-page="currentPage" :total-pages="totalPages" @change-page="changePage" />

            </div>
          </div>
        </div>

        <!-- Modal -->
        <teleport to="body">
          <div v-if="showModal" class="modal-overlay" @click="closeModal">
            <div class="modal-content" @click.stop>
              <h5 class="mb-3">{{ isEditing ? "Editar Amenidad" : "Agregar Amenidad" }}</h5>
              <!-- <argon-alert v-if="showModal && errorMessage" color="alert-danger" icon="ni ni-fat-remove" class="mb-3">
                {{ errorMessage }}
              </argon-alert> -->

              <div v-if="errorMessage" class="text-center text-danger py-3">
                {{ errorMessage }}
              </div>
              <div class="mb-3">
                <label>Nombre</label>
                <input v-model="selectedAmenity.name" class="form-control" type="text" required />
              </div>
              <div class="mb-3">
                <label>Descripción</label>
                <textarea v-model="selectedAmenity.description" class="form-control" required></textarea>
              </div>
              <div class="d-flex justify-content-end">
                <argon-button color="secondary" class="me-2" @click="closeModal">Cancelar</argon-button>
                <argon-button color="primary" @click="saveAmenity">
                  {{ isEditing ? "Guardar Cambios" : "Agregar" }}
                </argon-button>
              </div>
            </div>
          </div>
        </teleport>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.3);
  z-index: 1051;
  width: 500px;
  max-width: 90%;
  position: relative;
}
</style>
